import { createElement, createNode } from "../utils/dom";
import { AgentWidgetConfig, AgentWidgetSuggestionSurface } from "../types";
import { suggestionSurfaceAttr } from "./suggestions";
import { positionMap } from "../utils/positioning";
import { isComposerBarMountMode, isDockedMountMode } from "../utils/dock";
import { DEFAULT_OVERLAY_Z_INDEX } from "../utils/constants";
import { buildHeader, attachHeaderToContainer, HeaderElements } from "./header-builder";
import { buildHeaderWithLayout } from "./header-layouts";
import { createCloseButton, createClearChatButton } from "./header-parts";
import { buildComposer, ComposerElements } from "./composer-builder";
import { buildPillComposer, buildPillPeekBanner } from "./pill-composer-builder";
import {
  applyWelcomeConfig,
  applyWelcomeVisibility,
  buildGreetingHost,
  buildWelcomeHost,
  renderWelcomeGreeting,
  WelcomeHostElements,
} from "./welcome";
import { isWelcomeVisible, resolveWelcomeConfig } from "../welcome";
import { resolveContentMaxWidth } from "../utils/content-width";
import { getPanelAliasProvenance, isHeaderVisible, PANEL_WIDTH_VAR } from "../utils/panel-config";

const PANEL_MAX_WIDTH_VAR = "var(--persona-components-panel-maxWidth)";
const PANEL_HEIGHT_VAR = "var(--persona-components-panel-height)";
const PANEL_MAX_HEIGHT_VAR = "var(--persona-components-panel-maxHeight)";

export type PanelGeometry = {
  width: string;
  maxWidth: string;
  height: string;
  maxHeight: string;
};

/**
 * Legacy aliases win when a caller explicitly supplied them; otherwise the
 * resolved panel token variables own geometry. Provenance keeps a materialized
 * launcher default from masquerading as a legacy override after config merge.
 */
export const resolvePanelGeometry = (config?: AgentWidgetConfig): PanelGeometry => {
  const aliases = getPanelAliasProvenance(config);
  const legacyWidth = aliases.launcherWidth
    ? config?.launcher?.width
    : aliases.legacyLauncherWidth
      ? config?.launcherWidth
      : undefined;
  const heightOffset = config?.launcher?.heightOffset ?? 0;
  const height = heightOffset === 0
    ? `max(200px, ${PANEL_HEIGHT_VAR})`
    : `max(200px, calc(${PANEL_HEIGHT_VAR} - ${heightOffset}px))`;
  return {
    width: legacyWidth ?? PANEL_WIDTH_VAR,
    maxWidth: legacyWidth ?? PANEL_MAX_WIDTH_VAR,
    height,
    maxHeight: PANEL_MAX_HEIGHT_VAR,
  };
};

/** Inline embeds fill their host unless the caller explicitly supplied a width. */
export const resolveInlinePanelWidth = (config?: AgentWidgetConfig): string => {
  const aliases = getPanelAliasProvenance(config);
  const hasWidth = aliases.launcherWidth || aliases.legacyLauncherWidth ||
    config?.theme?.components?.panel?.width !== undefined;
  return hasWidth ? resolvePanelGeometry(config).width : "100%";
};

/**
 * Composer-bar chrome sizes. Not header controls: these float over the
 * composer, so they opt out of the header control-size token with explicit
 * per-control overrides. `CLOSE_ICON_SIZE` is the X's nominal size before the
 * factory's sparse-viewBox compensation (10 x 1.4 = the rendered 14px).
 */
export const COMPOSER_BAR_CONTROL_SIZE = "16px";
export const COMPOSER_BAR_CLOSE_ICON_SIZE = "10px";
export const COMPOSER_BAR_CLEAR_CHAT_ICON_SIZE = "14px";

export interface PanelWrapper {
  wrapper: HTMLElement;
  panel: HTMLElement;
  /**
   * Composer-bar mode only: viewport-fixed sibling of `wrapper` that owns
   * the persistent pill (`footer`) and peek banner. Lives outside the
   * wrapper so it never inherits the wrapper's geometry transitions:   * critical for modal mode where the wrapper is `transform: translate(-50%, -50%)`
   * (a transformed ancestor establishes a containing block for `position: fixed`
   * descendants, which would trap the pill inside the wrapper otherwise).
   */
  pillRoot?: HTMLElement;
}

export const createWrapper = (config?: AgentWidgetConfig): PanelWrapper => {
  const launcherEnabled = config?.launcher?.enabled ?? true;
  const dockedMode = isDockedMountMode(config);
  const composerBarMode = isComposerBarMountMode(config);

  if (composerBarMode) {
    const cb = config?.launcher?.composerBar ?? {};
    // Geometry (left/transform/bottom/top/width/max-width) is intentionally
    // NOT set here: it's owned entirely by `applyComposerBarGeometry()` in
    // ui.ts so that collapsed → expanded transitions can clear the previous
    // state's inline styles cleanly. Setting geometry here would persist
    // across state changes and override the per-state values, which
    // previously caused expanded panels to render at collapsed dimensions.
    const wrapper = createElement(
      "div",
      "persona-widget-wrapper persona-fixed persona-transition"
    );
    wrapper.setAttribute("data-persona-composer-bar", "");
    wrapper.dataset.state = "collapsed";
    wrapper.dataset.expandedSize = cb.expandedSize ?? "anchored";
    wrapper.style.zIndex = String(
      config?.launcher?.zIndex ?? DEFAULT_OVERLAY_Z_INDEX
    );

    const panel = createElement(
      "div",
      "persona-widget-panel persona-relative persona-flex persona-flex-1 persona-min-h-0 persona-flex-col"
    );
    panel.style.width = "100%";
    wrapper.appendChild(panel);

    // Pill lives in a separate viewport-fixed sibling: see PanelWrapper
    // docs above. ui.ts appends `peekBanner` and `footer` here, then
    // appends pillRoot to `mount` immediately after the wrapper.
    const pillRoot = createElement("div", "persona-widget-pill-root");
    pillRoot.setAttribute("data-persona-composer-bar", "");
    pillRoot.dataset.state = "collapsed";
    pillRoot.dataset.expandedSize = cb.expandedSize ?? "anchored";
    pillRoot.style.zIndex = String(
      config?.launcher?.zIndex ?? DEFAULT_OVERLAY_Z_INDEX
    );

    return { wrapper, panel, pillRoot };
  }

  if (dockedMode) {
    const wrapper = createElement(
      "div",
      "persona-relative persona-h-full persona-w-full persona-flex persona-flex-1 persona-min-h-0 persona-flex-col"
    );
    const panel = createElement(
      "div",
      "persona-relative persona-h-full persona-w-full persona-flex persona-flex-1 persona-min-h-0 persona-flex-col"
    );

    wrapper.appendChild(panel);
    return { wrapper, panel };
  }

  if (!launcherEnabled) {
    // For inline embed mode, use flex layout to ensure the widget fills its container
    // and only the chat messages area scrolls
    const wrapper = createElement(
      "div",
      "persona-relative persona-h-full persona-flex persona-flex-col persona-flex-1 persona-min-h-0"
    );
    const panel = createElement(
      "div",
      "persona-relative persona-flex-1 persona-flex persona-flex-col persona-min-h-0"
    );
    
    // Apply width from config, defaulting to 100% for inline embed mode
    wrapper.style.width = resolveInlinePanelWidth(config);
    panel.style.width = "100%";
    
    wrapper.appendChild(panel);
    return { wrapper, panel };
  }

  const launcher = config?.launcher ?? {};
  const position =
    launcher.position && positionMap[launcher.position]
      ? positionMap[launcher.position]
      : positionMap["bottom-right"];

  const wrapper = createElement(
    "div",
    `persona-widget-wrapper persona-fixed ${position} persona-transition`
  );
  wrapper.style.zIndex = String(config?.launcher?.zIndex ?? DEFAULT_OVERLAY_Z_INDEX);

  const panel = createElement(
    "div",
    "persona-widget-panel persona-relative persona-min-h-[320px]"
  );
  const geometry = resolvePanelGeometry(config);
  panel.style.width = geometry.width;
  panel.style.maxWidth = geometry.maxWidth;

  wrapper.appendChild(panel);
  return { wrapper, panel };
};

export interface PanelElements {
  container: HTMLElement;
  body: HTMLElement;
  messagesWrapper: HTMLElement;
  /** Starter prompts rendered inside the welcome card. */
  starterSuggestions: HTMLElement;
  /** Follow-up suggestions rendered after the transcript. */
  transcriptSuggestions: HTMLElement;
  /**
   * Absolute-positioned slot above the composer footer. Interactive sheets
   * (e.g. the answer-pill sheet for the ask_user_question tool) mount here
   * so they slide in without reflowing the chat transcript.
   */
  composerOverlay: HTMLElement;
  suggestions: HTMLElement;
  textarea: HTMLTextAreaElement;
  sendButton: HTMLButtonElement;
  sendButtonWrapper: HTMLElement;
  micButton: HTMLButtonElement | null;
  micButtonWrapper: HTMLElement | null;
  composerForm: HTMLFormElement;
  statusText: HTMLElement;
  /** Permanent welcome host: variant and visibility change on it in place. */
  welcomeHost: HTMLElement;
  welcomeIconHolder: HTMLElement;
  /** Display-only greeting bubble host, pinned above the transcript. */
  greetingHost: HTMLElement;
  introKicker: HTMLElement;
  introTitle: HTMLElement;
  introSubtitle: HTMLElement;
  closeButton: HTMLButtonElement;
  closeButtonWrapper: HTMLElement;
  clearChatButton: HTMLButtonElement | null;
  clearChatButtonWrapper: HTMLElement | null;
  iconHolder: HTMLElement;
  headerTitle: HTMLElement;
  headerSubtitle: HTMLElement;
  // Exposed for potential header replacement
  header: HTMLElement;
  footer: HTMLElement;
  // Attachment elements
  attachmentButton: HTMLButtonElement | null;
  attachmentButtonWrapper: HTMLElement | null;
  attachmentInput: HTMLInputElement | null;
  attachmentPreviewsContainer: HTMLElement | null;
  // Actions row layout elements
  actionsRow: HTMLElement;
  leftActions: HTMLElement;
  rightActions: HTMLElement;
  /** Swap the send button between its send and stop appearances. */
  setSendButtonMode: (mode: "send" | "stop") => void;
  /**
   * Composer-bar peek banner: the chrome-less row above the pill that
   * shows a trailing preview of the most recent assistant message.
   * Undefined for non-composer-bar modes.
   */
  peekBanner?: HTMLButtonElement;
  peekTextNode?: HTMLElement;
}

const createSuggestionHost = (
  surface: AgentWidgetSuggestionSurface
): HTMLElement => {
  const host = createNode("div", {
    className: "persona-suggestions",
    attrs: { "data-persona-suggestions": suggestionSurfaceAttr(surface) },
  });
  host.hidden = true;
  return host;
};

/**
 * Welcome surface for either panel builder: permanent host plus the greeting
 * host that sits between it and the transcript.
 */
const buildWelcomeSurface = (
  config: AgentWidgetConfig | undefined,
  body: HTMLElement
): { welcome: WelcomeHostElements; greetingHost: HTMLElement } => {
  const starterSuggestions = createSuggestionHost("starter");
  const welcome = buildWelcomeHost(config, starterSuggestions, {
    flatShadow: isDockedMountMode(config),
  });
  const resolved = resolveWelcomeConfig(config);
  applyWelcomeConfig(welcome, resolved);
  applyWelcomeVisibility(
    body,
    welcome.host,
    isWelcomeVisible(resolved, config?.initialMessages)
  );
  const greetingHost = buildGreetingHost();
  renderWelcomeGreeting(greetingHost, resolved.message, config);
  return { welcome, greetingHost };
};

/**
 * Composer-bar panel: minimal close-only header (× in the top-right of the
 * chat chrome). The pill (`footer`) and `peekBanner` are NOT children of
 * the panel: caller (`ui.ts`) appends them to the `pillRoot` returned by
 * `createWrapper`, which is a viewport-fixed sibling of the wrapper.
 * This decouples the pill from the wrapper's geometry transitions (so the
 * pill stays anchored at the viewport bottom regardless of expanded mode).
 */
const buildComposerBarPanel = (
  config: AgentWidgetConfig | undefined,
  showClose: boolean,
): PanelElements => {
  // Container = the chat panel chrome (border + bg + radius + shadow applied
  // inline by `applyFullHeightStyles` in ui.ts). `position: relative` anchors
  // the absolute close button + composerOverlay.
  const container = createElement(
    "div",
    "persona-widget-container persona-relative persona-flex persona-flex-1 persona-min-h-0 persona-flex-col persona-text-persona-primary"
  );
  container.setAttribute("data-persona-theme-zone", "container");

  // Minimal header: just an absolutely-positioned close button.
  // The wrapper uses inline styles (top/right/z-index values) because the
  // widget's hand-authored CSS doesn't ship every Tailwind utility.
  // Composer-bar's close is a 16px box, far below the header control token,
  // to match the minimal aesthetic; the user can still override via
  // `launcher.closeButtonSize`.
  const { button: closeButton, wrapper: closeButtonWrapper } = createCloseButton(
    config,
    {
      showClose,
      wrapperClassName: "persona-composer-bar-close",
      buttonSize: COMPOSER_BAR_CONTROL_SIZE,
      iconSize: COMPOSER_BAR_CLOSE_ICON_SIZE,
    }
  );
  closeButtonWrapper.style.position = "absolute";
  closeButtonWrapper.style.top = "8px";
  closeButtonWrapper.style.right = "8px";
  closeButtonWrapper.style.zIndex = "10";

  // Clear / "start over" button: sits immediately to the left of the close
  // button in the panel chrome's top-right corner. Same minimal sizing as
  // the close icon (16px button, 14px icon) so the two read as a paired
  // action group rather than a header strip. Wired by `setupClearChatButton()`
  // in ui.ts via the `clearChatButton` field on PanelElements.
  //
  // Right offset = close button right (8px) + close button width (16px) +
  // 8px inter-button gap = 32px.
  const clearChatEnabled = config?.launcher?.clearChat?.enabled ?? true;
  let clearChatButton: HTMLButtonElement | null = null;
  let clearChatButtonWrapper: HTMLElement | null = null;
  if (clearChatEnabled) {
    const parts = createClearChatButton(config, {
      wrapperClassName: "persona-composer-bar-clear-chat",
      buttonSize: COMPOSER_BAR_CONTROL_SIZE,
      iconSize: COMPOSER_BAR_CLEAR_CHAT_ICON_SIZE,
    });
    clearChatButton = parts.button;
    clearChatButtonWrapper = parts.wrapper;
    clearChatButtonWrapper.style.position = "absolute";
    clearChatButtonWrapper.style.top = "8px";
    clearChatButtonWrapper.style.right = "32px";
    clearChatButtonWrapper.style.zIndex = "10";
  }
  // Placeholder header element so PanelElements.header exists (some downstream
  // code reads it). It carries `data-persona-widget-header` for plugin /
  // selector parity but renders nothing visible: the close button is the only
  // header chrome in composer-bar mode.
  const headerPlaceholder = createNode("span", {
    className: "persona-widget-header",
    attrs: { "data-persona-theme-zone": "header" },
    style: { display: "none" },
  });

  // Body: extra top padding (set inline so the hand-authored widget.css
  // doesn't need a `pt-12` utility) so the absolute close button doesn't
  // overlap the welcome card / first message.
  const body = createNode("div", {
    className:
      "persona-widget-body persona-flex persona-flex-1 persona-min-h-0 persona-flex-col persona-gap-6 persona-overflow-y-auto persona-bg-persona-container persona-px-6 persona-py-6",
    attrs: { id: "persona-scroll-container", "data-persona-theme-zone": "messages" },
    style: { paddingTop: "48px" },
  });
  // Reserve the scrollbar gutter so the transcript doesn't shift horizontally
  // when streaming content first overflows and the scrollbar appears.
  body.style.setProperty("scrollbar-gutter", "stable");

  const { welcome, greetingHost } = buildWelcomeSurface(config, body);
  const { host: welcomeHost, title: introTitle, subtitle: introSubtitle } =
    welcome;
  const starterSuggestions = welcome.starterSuggestions;

  const messagesWrapper = createElement(
    "div",
    "persona-widget-messages persona-flex persona-flex-col"
  );
  messagesWrapper.style.gap = "var(--persona-components-message-gap, 12px)";
  const transcriptSuggestions = createSuggestionHost("followUp");
  const contentMaxWidth = resolveContentMaxWidth(config, true);
  if (contentMaxWidth) {
    messagesWrapper.style.maxWidth = contentMaxWidth;
    messagesWrapper.style.marginLeft = "auto";
    messagesWrapper.style.marginRight = "auto";
    messagesWrapper.style.width = "100%";
  }

  body.append(
    welcomeHost,
    greetingHost,
    messagesWrapper,
    transcriptSuggestions
  );

  // Composer overlay (interactive sheets like ask_user_question slide up here).
  // Anchored to the bottom of the container (which is `position: relative`),
  // so sheets render at the bottom of the chat chrome: just above the gap +
  // pill that sit below the container.
  const composerOverlay = createNode("div", {
    className: "persona-composer-overlay persona-pointer-events-none",
    attrs: { "data-persona-composer-overlay": "" },
    style: {
      position: "absolute",
      left: "0",
      right: "0",
      // Both vars default to 0px, so `block` placement is unchanged.
      bottom:
        "calc(var(--persona-composer-overlay-height, 0px) + var(--persona-composer-lift, 0px))",
      zIndex: "20",
    },
  });

  // Pill composer: caller appends as a sibling of container in the panel.
  const composerElements: ComposerElements = buildPillComposer({ config });

  // Peek banner: caller inserts as a sibling of container/footer between
  // them in the panel. Hidden by default; ui.ts toggles visibility.
  const { root: peekBanner, textNode: peekTextNode } = buildPillPeekBanner();

  // Container = [close button (absolute), placeholder header, body, overlay].
  // Footer (pill) is intentionally NOT appended here.
  container.append(headerPlaceholder, closeButtonWrapper, body, composerOverlay);
  if (clearChatButtonWrapper) {
    container.appendChild(clearChatButtonWrapper);
  }

  return {
    container,
    body,
    messagesWrapper,
    starterSuggestions,
    transcriptSuggestions,
    composerOverlay,
    suggestions: composerElements.suggestions,
    textarea: composerElements.textarea,
    sendButton: composerElements.sendButton,
    sendButtonWrapper: composerElements.sendButtonWrapper,
    micButton: composerElements.micButton,
    micButtonWrapper: composerElements.micButtonWrapper,
    composerForm: composerElements.composerForm,
    statusText: composerElements.statusText,
    welcomeHost,
    welcomeIconHolder: welcome.iconHolder,
    greetingHost,
    introKicker: welcome.kicker,
    introTitle,
    introSubtitle,
    closeButton,
    closeButtonWrapper,
    clearChatButton,
    clearChatButtonWrapper,
    iconHolder: createElement("span"),
    headerTitle: createElement("span"),
    headerSubtitle: createElement("span"),
    header: headerPlaceholder,
    footer: composerElements.footer,
    attachmentButton: composerElements.attachmentButton,
    attachmentButtonWrapper: composerElements.attachmentButtonWrapper,
    attachmentInput: composerElements.attachmentInput,
    attachmentPreviewsContainer: composerElements.attachmentPreviewsContainer,
    actionsRow: composerElements.actionsRow,
    leftActions: composerElements.leftActions,
    rightActions: composerElements.rightActions,
    setSendButtonMode: composerElements.setSendButtonMode,
    peekBanner,
    peekTextNode,
  };
};

export const buildPanel = (config?: AgentWidgetConfig, showClose = true): PanelElements => {
  // Composer-bar mode renders a purpose-built panel: minimal close-only
  // header (small × in the top-right of the chat panel chrome), and the
  // pill (footer) is a SIBLING of the container so it stays visible/usable
  // when the chat is expanded above it.
  if (isComposerBarMountMode(config)) {
    return buildComposerBarPanel(config, showClose);
  }

  const container = createNode("div", {
    className:
      "persona-widget-container persona-flex persona-h-full persona-w-full persona-flex-1 persona-min-h-0 persona-flex-col persona-text-persona-primary persona-bg-persona-surface persona-rounded-2xl persona-overflow-hidden persona-border persona-border-persona-border",
    attrs: { "data-persona-theme-zone": "container" },
  });

  // Build header using layout config if available, otherwise use standard builder
  const headerLayoutConfig = config?.layout?.header;
  const showHeader = isHeaderVisible(config);
  const headerElements: HeaderElements = headerLayoutConfig
    ? buildHeaderWithLayout(config!, headerLayoutConfig, { showClose })
    : buildHeader({ config, showClose });

  // Build body with intro card and messages wrapper
  const body = createNode("div", {
    className:
      "persona-widget-body persona-flex persona-flex-1 persona-min-h-0 persona-flex-col persona-gap-6 persona-overflow-y-auto persona-bg-persona-container persona-px-6 persona-py-6",
    attrs: { id: "persona-scroll-container", "data-persona-theme-zone": "messages" },
  });
  // Reserve the scrollbar gutter so the transcript doesn't shift horizontally
  // when streaming content first overflows and the scrollbar appears.
  body.style.setProperty("scrollbar-gutter", "stable");

  const { welcome, greetingHost } = buildWelcomeSurface(config, body);
  const { host: welcomeHost, title: introTitle, subtitle: introSubtitle } =
    welcome;
  const starterSuggestions = welcome.starterSuggestions;

  const messagesWrapper = createElement(
    "div",
    "persona-widget-messages persona-flex persona-flex-col"
  );
  messagesWrapper.style.gap = "var(--persona-components-message-gap, 12px)";
  const transcriptSuggestions = createSuggestionHost("followUp");

  const contentMaxWidth = resolveContentMaxWidth(config, false);
  if (contentMaxWidth) {
    messagesWrapper.style.maxWidth = contentMaxWidth;
    messagesWrapper.style.marginLeft = "auto";
    messagesWrapper.style.marginRight = "auto";
    messagesWrapper.style.width = "100%";
  }

  body.append(
    welcomeHost,
    greetingHost,
    messagesWrapper,
    transcriptSuggestions
  );

  // composer-bar mode early-returned above with its own pill builder; this
  // path always uses the standard column-stacked composer card.
  const composerElements: ComposerElements = buildComposer({ config });
  const showFooter = config?.layout?.showFooter !== false; // default to true

  // Assemble container with header, body, and footer
  if (showHeader) {
    attachHeaderToContainer(container, headerElements, config);
  } else {
    // Hide header completely
    headerElements.header.style.display = 'none';
    attachHeaderToContainer(container, headerElements, config);
  }
  
  container.append(body);
  
  // Composer overlay slot: sits between body and footer, absolutely positioned
  // above the composer so sheets (e.g. the ask_user_question answer-pill sheet)
  // can slide up without reflowing the chat transcript above. Uses inline
  // styles for left/right/bottom because widget.css is hand-authored and
  // doesn't ship `.persona-left-0` / `.persona-right-0` rules: without
  // them the overlay shrink-wraps to content and collapses the sheet width.
  // zIndex 20 sits above .persona-scroll-to-bottom-indicator (z-index 10,
  // sibling in the container) so suggestion chips and the ask-user-question
  // sheet are not covered by the "jump to latest" button.
  const composerOverlay = createNode("div", {
    className: "persona-composer-overlay persona-pointer-events-none",
    attrs: { "data-persona-composer-overlay": "" },
    style: {
      position: "absolute",
      left: "0",
      right: "0",
      // Both vars default to 0px, so `block` placement is unchanged.
      bottom:
        "calc(var(--persona-composer-overlay-height, 0px) + var(--persona-composer-lift, 0px))",
      zIndex: "20",
    },
  });

  if (showFooter) {
    container.append(composerElements.footer);
  } else {
    // Hide footer completely
    composerElements.footer.style.display = 'none';
    container.append(composerElements.footer);
  }

  // Append overlay last so it stacks above the footer / body content.
  container.append(composerOverlay);

  return {
    container,
    body,
    messagesWrapper,
    starterSuggestions,
    transcriptSuggestions,
    composerOverlay,
    suggestions: composerElements.suggestions,
    textarea: composerElements.textarea,
    sendButton: composerElements.sendButton,
    sendButtonWrapper: composerElements.sendButtonWrapper,
    micButton: composerElements.micButton,
    micButtonWrapper: composerElements.micButtonWrapper,
    composerForm: composerElements.composerForm,
    statusText: composerElements.statusText,
    welcomeHost,
    welcomeIconHolder: welcome.iconHolder,
    greetingHost,
    introKicker: welcome.kicker,
    introTitle,
    introSubtitle,
    closeButton: headerElements.closeButton,
    closeButtonWrapper: headerElements.closeButtonWrapper,
    clearChatButton: headerElements.clearChatButton,
    clearChatButtonWrapper: headerElements.clearChatButtonWrapper,
    iconHolder: headerElements.iconHolder,
    headerTitle: headerElements.headerTitle,
    headerSubtitle: headerElements.headerSubtitle,
    header: headerElements.header,
    footer: composerElements.footer,
    // Attachment elements
    attachmentButton: composerElements.attachmentButton,
    attachmentButtonWrapper: composerElements.attachmentButtonWrapper,
    attachmentInput: composerElements.attachmentInput,
    attachmentPreviewsContainer: composerElements.attachmentPreviewsContainer,
    // Actions row layout elements
    actionsRow: composerElements.actionsRow,
    leftActions: composerElements.leftActions,
    rightActions: composerElements.rightActions,
    setSendButtonMode: composerElements.setSendButtonMode
  };
};

// Re-export builder types and functions for plugin use
export { buildHeader, buildComposer, attachHeaderToContainer };
export type { HeaderElements, HeaderBuildContext } from "./header-builder";
export type { ComposerElements, ComposerBuildContext } from "./composer-builder";
